import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { StatusChip, Chip } from "@/components/StatusChip";
import {
  CheckCircle2, XCircle, AlertTriangle, ChevronDown, Pencil, Check, X,
  Users, CalendarDays, ImageOff, Send, Sparkles, Loader2,
} from "lucide-react";
import { api, fmtUSD, fmtDate, fmtTime, GL_OPTIONS, CATEGORY_OPTIONS, timeAgo, POLICY_STATUS_META } from "@/lib/api";
import { PersonAvatar } from "@/components/PersonAvatar";
import { AriaMark } from "@/components/AriaMark";
import { toast } from "sonner";

const CHECK_ICON = {
  pass: <CheckCircle2 size={13} className="text-[#4ADE80]" />,
  fail: <XCircle size={13} className="text-[#F87171]" />,
  warn: <AlertTriangle size={13} className="text-[#FBBF24]" />,
};

const DOT_TYPE = {
  system: "bg-[#555]",
  aria: "bg-[#4F8EF7]",
  employee: "bg-[#A3A3A3]",
  approval: "bg-[#22C55E]",
  violation: "bg-[#EF4444]",
};

const PSTATUS_META = POLICY_STATUS_META;

const DetailRow = ({ label, children, onClick, testId }) => (
  <div className="flex items-center justify-between gap-3 px-3.5 py-2.5">
    <span className="shrink-0 text-[11px] text-[#6b6b6b]">{label}</span>
    {onClick ? (
      <button onClick={onClick} data-testid={testId}
        className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-[#e5e5e5] transition-colors hover:text-[#7FAEF9] hover:underline">
        {children}
      </button>
    ) : (
      <span className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-[#e5e5e5]" data-testid={testId}>{children}</span>
    )}
  </div>
);

const Section = ({ title, children, right }) => (
  <div className="border-t border-[#1e1e1e] px-5 py-4">
    <div className="mb-2.5 flex items-center justify-between">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-[#6b6b6b]">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

export const ExpenseDetailDrawer = ({ expenseId, open, onClose, onChanged, onFilter }) => {
  const navigate = useNavigate();
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editCategory, setEditCategory] = useState("");
  const [editGl, setEditGl] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState([]);
  const [summarizing, setSummarizing] = useState(false);

  useEffect(() => {
    if (open && members.length === 0) {
      api.get("/workspace/members").then((r) => setMembers(r.data)).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const assignController = async (memberId) => {
    try {
      const r = await api.post("/expenses/assign", { ids: [exp.id], assignee_id: memberId });
      setExp({ ...exp, assigned_to: r.data.assigned_to });
      onChanged?.();
      toast.success(r.data.assigned_to ? `Assigned to ${r.data.assigned_to.name}` : "Unassigned");
    } catch {
      toast.error("Assignment failed");
    }
  };

  const genSummary = async () => {
    setSummarizing(true);
    try {
      const r = await api.post(`/expenses/${exp.id}/summary`);
      setExp({ ...exp, aria_summary: r.data });
    } catch {
      toast.error("Could not generate the summary");
    } finally {
      setSummarizing(false);
    }
  };

  useEffect(() => {
    if (open && expenseId) {
      setLoading(true);
      setExp(null);
      api.get(`/expenses/${expenseId}`)
        .then((r) => {
          setExp(r.data);
          setEditCategory(r.data.category);
          setEditGl(r.data.gl_code);
        })
        .catch(() => toast.error("Could not load expense"))
        .finally(() => setLoading(false));
    }
    if (!open) setEditing(false);
  }, [open, expenseId]);

  const saveEdits = async () => {
    const gl = GL_OPTIONS.find((g) => g.code === editGl);
    setBusy(true);
    try {
      const r = await api.patch(`/expenses/${exp.id}`, {
        category: editCategory, gl_code: editGl, gl_name: gl?.name,
      });
      setExp(r.data);
      setEditing(false);
      onChanged?.();
      toast.success("Expense updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post("/expenses/bulk", { ids: [exp.id], action });
      const r = await api.get(`/expenses/${exp.id}`);
      setExp(r.data);
      onChanged?.();
      toast.success(action === "approve" ? "Approved" : action === "reject" ? "Rejected" : action === "message" ? "Aria is following up with the employee" : "Done");
    } catch {
      toast.error("Action failed");
    } finally {
      setBusy(false);
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      const r = await api.post(`/expenses/${exp.id}/comments`, { text: comment });
      setExp({ ...exp, comments: [...(exp.comments || []), r.data] });
      setComment("");
    } catch {
      toast.error("Could not add comment");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-[#222] bg-[#111111] p-0 text-white sm:max-w-[540px]" data-testid="expense-detail-drawer">
        <SheetTitle className="sr-only">Expense details</SheetTitle>
        <SheetDescription className="sr-only">Full expense breakdown, policy checks and audit timeline</SheetDescription>
        {loading || !exp ? (
          <div className="space-y-4 p-6">
            <Skeleton className="h-8 w-2/3 bg-[#1c1c1c]" />
            <Skeleton className="h-40 w-full bg-[#1c1c1c]" />
            <Skeleton className="h-24 w-full bg-[#1c1c1c]" />
            <Skeleton className="h-24 w-full bg-[#1c1c1c]" />
          </div>
        ) : (
          <>
            {/* header */}
            <div className="px-5 pb-4 pt-5">
              <div className="flex items-start justify-between gap-3 pr-8">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold tracking-tight" data-testid="drawer-merchant">{exp.merchant}</h2>
                  <StatusChip status={exp.status} />
                </div>
                <div className="text-xl font-semibold tabular-nums">{fmtUSD(exp.amount)}</div>
              </div>

              {/* transaction details */}
              <div className="mt-3 divide-y divide-[#1a1a1a] rounded-xl border border-[#1e1e1e] bg-[#141414]" data-testid="drawer-detail-rows">
                <DetailRow label="Employee" testId="drawer-detail-employee"
                  onClick={exp.employee_id ? () => { onClose(); navigate(`/employees/${exp.employee_id}`); } : undefined}>
                  <PersonAvatar name={exp.employee_name} size={18} />
                  <span className="truncate">{exp.employee_name}</span>
                </DetailRow>
                <DetailRow label="Department" testId="drawer-detail-department"
                  onClick={onFilter ? () => onFilter("department", exp.department) : undefined}>
                  {exp.department}
                </DetailRow>
                <DetailRow label="Date" testId="drawer-detail-date"
                  onClick={onFilter ? () => onFilter("date", String(exp.date).slice(0, 10)) : undefined}>
                  <span className="tabular-nums">{fmtDate(exp.date)}</span>
                </DetailRow>
                <DetailRow label="Category" testId="drawer-detail-category"
                  onClick={onFilter ? () => onFilter("category", exp.category) : undefined}>
                  {exp.category}
                </DetailRow>
                <DetailRow label="In Policy" testId="drawer-detail-policy"
                  onClick={onFilter && exp.policy_id ? () => onFilter("policy", exp.policy_id) : undefined}>
                  <span className="max-w-[210px] truncate">{exp.policy_name || "—"}</span>
                  <Chip tone={(PSTATUS_META[exp.policy_status] || { tone: "neutral" }).tone}>
                    {(PSTATUS_META[exp.policy_status] || { label: exp.policy_status }).label}
                  </Chip>
                </DetailRow>
              </div>

              <div className="mt-3 flex gap-2">
                {(exp.status === "needs_review" || exp.status === "waiting_on_employee" || exp.status === "draft") && (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => act("approve")} data-testid="drawer-approve-button"
                      className="h-7 bg-[#22C55E] px-3 text-[12px] font-medium text-black hover:bg-[#2fd66c]">
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => act("reject")} data-testid="drawer-reject-button"
                      className="h-7 border-[#2a2a2a] bg-transparent px-3 text-[12px] text-[#F87171] hover:bg-[rgba(239,68,68,0.08)] hover:text-[#F87171]">
                      Reject
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" disabled={busy} onClick={() => act("message")} data-testid="drawer-aria-followup-button"
                  className="h-7 border-[rgba(79,142,247,0.35)] bg-[rgba(79,142,247,0.1)] px-3 text-[12px] text-[#7FAEF9] hover:bg-[rgba(79,142,247,0.18)] hover:text-white">
                  <Send size={11} className="mr-1" /> Aria follow up
                </Button>
              </div>
            </div>

            {/* receipt */}
            <Section title="Receipt">
              {exp.receipt_image ? (
                <img src={exp.receipt_image} alt="Receipt" data-testid="drawer-receipt-image"
                  className="max-h-[300px] w-full rounded-xl border border-[#222] object-contain bg-[#0d0d0d]" />
              ) : (
                <div className="flex items-center gap-3 rounded-xl border border-dashed border-[#2a2a2a] bg-[#141414] px-4 py-5">
                  <ImageOff size={16} className="text-[#555]" />
                  <div className="text-[12px] text-[#A3A3A3]">
                    Aria is waiting for {exp.employee_name.split(" ")[0]}'s receipt — a reminder was sent automatically.
                  </div>
                </div>
              )}
            </Section>

            {/* parsed fields */}
            <Section
              title="Parsed details"
              right={
                editing ? (
                  <div className="flex gap-1">
                    <button onClick={saveEdits} disabled={busy} data-testid="drawer-save-edit-button"
                      className="flex h-6 w-6 items-center justify-center rounded-lg bg-[rgba(34,197,94,0.15)] text-[#4ADE80] hover:bg-[rgba(34,197,94,0.25)]">
                      <Check size={12} />
                    </button>
                    <button onClick={() => setEditing(false)}
                      className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#1c1c1c] text-[#A3A3A3] hover:text-white">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setEditing(true)} data-testid="drawer-edit-button"
                    className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#1c1c1c] text-[#A3A3A3] hover:text-white">
                    <Pencil size={11} />
                  </button>
                )
              }
            >
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-[12.5px]">
                <div>
                  <div className="text-[10.5px] text-[#6b6b6b]">Category</div>
                  {editing ? (
                    <Select value={editCategory} onValueChange={setEditCategory}>
                      <SelectTrigger className="mt-1 h-7 border-[#2a2a2a] bg-[#161616] text-[12px]" data-testid="drawer-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-[#222] bg-[#161616] text-white">
                        {CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-0.5 text-white">{exp.category}</div>
                  )}
                </div>
                <div>
                  <div className="text-[10.5px] text-[#6b6b6b]">GL assignment</div>
                  {editing ? (
                    <Select value={editGl} onValueChange={setEditGl}>
                      <SelectTrigger className="mt-1 h-7 border-[#2a2a2a] bg-[#161616] text-[12px]" data-testid="drawer-gl-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-[#222] bg-[#161616] text-white">
                        {GL_OPTIONS.map((g) => <SelectItem key={g.code} value={g.code}>{g.code} — {g.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-0.5 text-white">GL {exp.gl_code} · {exp.gl_name}</div>
                  )}
                </div>
                <div>
                  <div className="text-[10.5px] text-[#6b6b6b]">Assigned to</div>
                  <Select value={exp.assigned_to?.id || "none"} onValueChange={(v) => assignController(v === "none" ? null : v)}>
                    <SelectTrigger className="mt-1 h-7 border-[#2a2a2a] bg-[#161616] text-[12px]" data-testid="drawer-assign-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent className="border-[#222] bg-[#161616] text-white">
                      <SelectItem value="none">Unassigned</SelectItem>
                      {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.role}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><div className="text-[10.5px] text-[#6b6b6b]">Location</div><div className="mt-0.5 text-white">{exp.parsed?.location || "—"}</div></div>
                <div><div className="text-[10.5px] text-[#6b6b6b]">Payment</div><div className="mt-0.5 text-white">{exp.parsed?.payment_method || "—"}</div></div>
                <div><div className="text-[10.5px] text-[#6b6b6b]">Tax</div><div className="mt-0.5 tabular-nums text-white">{exp.parsed?.tax ? fmtUSD(exp.parsed.tax) : "—"}</div></div>
                <div><div className="text-[10.5px] text-[#6b6b6b]">Tip</div><div className="mt-0.5 tabular-nums text-white">{exp.parsed?.tip ? fmtUSD(exp.parsed.tip) : "—"}</div></div>
              </div>

              {exp.parsed?.line_items?.length > 0 && (
                <div className="mt-3 overflow-hidden rounded-xl border border-[#1e1e1e]">
                  {exp.parsed.line_items.map((li, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-1.5 text-[12px] ${i > 0 ? "border-t border-[#1a1a1a]" : ""}`}>
                      <span className="text-[#c9c9c9]">{li.description}</span>
                      <span className="tabular-nums text-white">{fmtUSD(li.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* context */}
            {(exp.attendees?.length > 0 || exp.calendar_event) && (
              <Section title="Context">
                {exp.calendar_event && (
                  <div className="mb-2 flex items-center gap-2 text-[12.5px] text-[#c9c9c9]">
                    <CalendarDays size={13} className="text-[#7FAEF9]" /> {exp.calendar_event}
                  </div>
                )}
                {exp.attendees?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Users size={13} className="text-[#7FAEF9]" />
                    <div className="flex flex-wrap gap-1.5">
                      {exp.attendees.map((a) => (
                        <span key={a} className="flex items-center gap-1.5 rounded-full border border-[#2a2a2a] bg-[#161616] py-0.5 pl-0.5 pr-2 text-[11px] text-[#c9c9c9]">
                          <PersonAvatar name={a} size={16} />
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Section>
            )}

            {/* policy checks */}
            <Section title="Policy checks">
              <div className="space-y-1.5">
                {exp.policy_checks?.map((c, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-[12.5px]">
                    <span className="mt-0.5">{CHECK_ICON[c.result]}</span>
                    <div>
                      <span className="text-white">{c.name}</span>
                      <span className="ml-2 text-[11.5px] text-[#6b6b6b]">{c.detail}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* AI reasoning */}
            <Section title="Aria's reasoning">
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="flex w-full items-center gap-2 text-left" data-testid="drawer-reasoning-toggle">
                  <AriaMark size={14} />
                  <span className="text-[12px] text-[#7FAEF9]">View reasoning</span>
                  <ChevronDown size={13} className="text-[#555]" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <p className="mt-2 rounded-xl border border-[rgba(79,142,247,0.15)] bg-[rgba(79,142,247,0.05)] px-3.5 py-3 text-[12px] leading-relaxed text-[#c9c9c9]">
                    {exp.ai_reasoning}
                  </p>
                </CollapsibleContent>
              </Collapsible>
            </Section>

            {/* comments */}
            <Section title={`Internal comments (${exp.comments?.length || 0})`}>
              <div className="space-y-2">
                {exp.comments?.map((c) => (
                  <div key={c.id} className="rounded-xl border border-[#1e1e1e] bg-[#141414] px-3 py-2">
                    <div className="flex items-baseline justify-between">
                      <span className="text-[11.5px] font-medium text-white">{c.author}</span>
                      <span className="text-[10px] text-[#555]">{timeAgo(c.ts)}</span>
                    </div>
                    <div className="mt-0.5 text-[12px] text-[#c9c9c9]">{c.text}</div>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input value={comment} onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addComment()}
                    placeholder="Add an internal note…" data-testid="drawer-comment-input"
                    className="h-8 border-[#2a2a2a] bg-[#161616] text-[12px] text-white placeholder:text-[#555]" />
                  <Button size="sm" onClick={addComment} data-testid="drawer-comment-submit"
                    className="h-8 bg-[#1f1f1f] px-3 text-[12px] text-white hover:bg-[#2a2a2a]">
                    Post
                  </Button>
                </div>
              </div>
            </Section>

            {/* aria expense summary + audit log */}
            <Section
              title="Aria expense summary"
              right={
                <button onClick={genSummary} disabled={summarizing} data-testid="drawer-generate-summary"
                  className="flex h-6 items-center gap-1.5 rounded-lg bg-[rgba(79,142,247,0.12)] px-2.5 text-[10.5px] font-medium text-[#7FAEF9] transition-colors hover:bg-[rgba(79,142,247,0.24)] disabled:opacity-50">
                  {summarizing ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                  {exp.aria_summary ? "Refresh" : "Generate"}
                </button>
              }
            >
              {exp.aria_summary ? (
                <div className="rounded-xl border border-[rgba(79,142,247,0.15)] bg-[rgba(79,142,247,0.05)] px-3.5 py-3" data-testid="drawer-aria-summary">
                  <div className="flex items-center gap-1.5">
                    <AriaMark size={13} />
                    <span className="text-[9.5px] font-semibold uppercase tracking-wider text-[#7FAEF9]">Digest of all comms & checks</span>
                    <span className="ml-auto text-[9.5px] text-[#555]">{timeAgo(exp.aria_summary.generated_at)}</span>
                  </div>
                  <p className="mt-1.5 text-[12px] leading-relaxed text-[#c9c9c9]">{exp.aria_summary.text}</p>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-[#2a2a2a] px-3.5 py-3 text-[11.5px] leading-relaxed text-[#6b6b6b]" data-testid="drawer-aria-summary-empty">
                  {summarizing
                    ? "Aria is reading the employee conversation, policy checks and audit history…"
                    : "Hit Generate and Aria will digest the employee conversation, policy checks and audit history into one paragraph."}
                </div>
              )}

              <div className="mt-3">
                <div className="mb-2 text-[9.5px] font-semibold uppercase tracking-wider text-[#555]">Audit log</div>
                <div className="space-y-0" data-testid="drawer-audit-timeline">
                  {exp.timeline?.map((t, i) => (
                    <div key={i} className="flex items-stretch gap-3">
                      <div className="flex w-14 shrink-0 justify-end pt-0.5 text-[10px] tabular-nums text-[#555]">{fmtTime(t.ts)}</div>
                      <div className="flex flex-col items-center">
                        <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${DOT_TYPE[t.type] || DOT_TYPE.system}`} />
                        {i < exp.timeline.length - 1 && <div className="w-px flex-1 bg-[#1e1e1e]" style={{ minHeight: 12 }} />}
                      </div>
                      <div className="pb-3 text-[12px] leading-5 text-[#c9c9c9]">{t.event}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Section>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
